import { useAllSalebills } from "http-hooks/salebill/useSalebill";
import { useViewShippingInfoReport } from "http-hooks/shippinginfo/useShippinginfo";
import DeleteShippingInfo from "components/shippingInfo/DeleteShippingInfo";
import { useState } from "react";
import { formatDateAndTime } from "utils/date";
import { SelectVendors, SelectCustomers } from "components/ui/selectors";
import AsyncItemSelect from "components/item/AsyncItemSelect";
import { ActionButton } from "components/ui/Table";
import InputSearch from "components/ui/InputSearch";
import {
  PencilSquareIcon,
  EyeIcon,
} from "@heroicons/react/24/outline";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getDocumentUrls, getDocumentViewerRoute } from "utils/documentUrlHelper";
import useAuth from "hooks/useAuth";
import useTableSort, { withSort } from "hooks/useTableSort";
import MasterPage, {
  FilterRow,
  FilterCell,
  ClearFiltersButton,
  AddButton,
  DeleteAction,
} from "components/ui/MasterPage";

const initialFilter = {
  salebillno: "",
  cusid: "",
  vendid: "",
  itemIds: "",
  enttp: "PL",
  page: 1,
  limit: 10,
};

const columns = [
  { key: "salebillno", header: "Shipping Info No.", sortable: true },
  {
    key: "salebilldate",
    header: "Date",
    sortable: true,
    render: (item) => formatDateAndTime(item.salebilldate),
  },
  {
    key: "customer",
    header: "Customer",
    sortable: true,
    render: (item) =>
      item.customer ? (
        <div className="font-medium">{item.customer.name}</div>
      ) : (
        item.cusid || "-"
      ),
  },
  {
    key: "vendor",
    header: "Vendor",
    sortable: true,
    render: (item) =>
      item.vendor ? (
        <div className="font-medium">{item.vendor.name}</div>
      ) : (
        item.vendid || "-"
      ),
  },
  { key: "actions", header: "Actions", isActions: true },
];

export default function ShippingInfo() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { agttp } = useAuth();
  const requestedTraderBillType = searchParams.get("traderbilltyp");
  const traderBillType =
    agttp === 1 && ["P", "S"].includes(requestedTraderBillType)
      ? requestedTraderBillType
      : agttp === 1
        ? "S"
        : "G";
  const challanName =
    agttp === 1
      ? traderBillType === "P"
        ? "Purchase Challan"
        : "Sale Challan"
      : "Shipping Info";
  const traderBillQuery =
    agttp === 1 ? `?traderbilltyp=${traderBillType}` : "";
  const [viewingReportId, setViewingReportId] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [filter, setFilter] = useState(initialFilter);
  const [searchShippingInfo, setSearchShippingInfo] = useState("");
  const tableSort = useTableSort();

  const handleEditSaleBill = (id) => {
    navigate(`/shippinginfo/${id}${traderBillQuery}`);
  };

  const { mutateAsync: viewReport } = useViewShippingInfoReport();

  const handleViewReport = async (salebill) => {
    try {
      setViewingReportId(salebill.salebillid);
      const data = await viewReport(salebill.salebillid);

      if (data.htmlUrl) {
        const { htmlUrl, pdfUrl } = getDocumentUrls(data);

        const route = getDocumentViewerRoute(salebill.salebillid, {
          htmlUrl,
          pdfUrl,
          documentNumber: salebill.salebillno,
          documentType: "Shipping Info",
        });

        navigate(route);
      }
    } catch (error) {
      console.error("Error viewing report:", error);
    } finally {
      setViewingReportId(null);
    }
  };

  const handleCustomerChange = (customer) => {
    setSelectedCustomer(customer);
    setFilter((prev) => ({
      ...prev,
      cusid: customer?.ps_Id || "",
    }));
  };

  const handleVendorChange = (vendor) => {
    setSelectedVendor(vendor);
    setFilter((prev) => ({
      ...prev,
      vendid: vendor?.ps_Id || "",
    }));
  };

  const handleItemsChange = (items) => {
    setSelectedItems(items);
    setFilter((prev) => ({
      ...prev,
      itemIds: items.map((item) => item.value).join(","),
    }));
  };

  const handleShippingInfoSearch = (searchTerm) => {
    setFilter((prev) => ({
      ...prev,
      salebillno: searchTerm,
    }));
  };

  const clearFilters = () => {
    setSelectedCustomer(null);
    setSelectedVendor(null);
    setSelectedItems([]);
    setSearchShippingInfo("");
    setFilter(initialFilter);
  };

  const {
    isLoading: isAllSalebillsLoading,
    data: { data: allsalebills, page: currentPage, totalPages } = [],
  } = useAllSalebills(
    withSort({ ...filter, traderbilltyp: traderBillType }, tableSort)
  );

  return (
    <MasterPage
      title={challanName}
      headerActions={
        <AddButton onClick={() => navigate(`/shippinginfo/new${traderBillQuery}`)}>
          Add {challanName}
        </AddButton>
      }
      filters={
        <>
          <FilterRow>
            <FilterCell>
              <InputSearch
                value={searchShippingInfo}
                onChange={(e) => setSearchShippingInfo(e.target.value)}
                onSearch={handleShippingInfoSearch}
                label="Shipping Info No."
                placeholder="Search by shipping info number"
              />
            </FilterCell>
            <FilterCell>
              <SelectCustomers
                value={selectedCustomer}
                onChange={handleCustomerChange}
                label="Customer"
                placeholder="Select Customer"
              />
            </FilterCell>
            <FilterCell>
              <SelectVendors
                value={selectedVendor}
                onChange={handleVendorChange}
                label="Vendor"
                placeholder="Select Vendor"
              />
            </FilterCell>
            <FilterCell>
              <AsyncItemSelect
                value={selectedItems}
                onChange={handleItemsChange}
                label="Items"
                placeholder="Search items..."
                isMulti={true}
              />
            </FilterCell>
          </FilterRow>
          <FilterRow>
            <ClearFiltersButton onClick={clearFilters} />
          </FilterRow>
        </>
      }
      columns={columns}
      sort={tableSort}
      data={allsalebills}
      isLoading={isAllSalebillsLoading}
      rowKey="salebillid"
      deleteComponent={DeleteShippingInfo}
      renderActions={(salebill, setDeleteItemId) => (
        <>
          <ActionButton
            variant="primary"
            onClick={() => handleViewReport(salebill)}
            title="View Report"
            disabled={viewingReportId === salebill.salebillid}
          >
            {viewingReportId === salebill.salebillid ? (
              <svg
                className="animate-spin h-4 w-4"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            ) : (
              <EyeIcon className="h-4 w-4" aria-hidden="true" />
            )}
          </ActionButton>
          <ActionButton
            variant="secondary"
            onClick={() => handleEditSaleBill(salebill.salebillid)}
            title={`Edit ${challanName}`}
          >
            <PencilSquareIcon className="h-4 w-4" aria-hidden="true" />
          </ActionButton>
          <DeleteAction
            onClick={() => setDeleteItemId(salebill.salebillid)}
            title={`Delete ${challanName}`}
          />
        </>
      )}
      pagination={{
        currentPage,
        totalPages,
        onPageChange: (page) => setFilter((prev) => ({ ...prev, page })),
      }}
    />
  );
}
